// Smart Daara - Course List Page for School Admin
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { courseAPI, userAPI } from '../../lib/api';
import { buildTenantPath } from '../../lib/routing';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { 
  BookOpen, Plus, Search, MoreVertical, Trash2, Pencil,
  Loader2, Eye, UserCog, Link2, BookMarked
} from 'lucide-react';
import { toast } from 'sonner';

const FILTER_STORAGE_KEY = 'courseListFilters';

export default function CourseList() {
  const { user } = useAuth();
  const { language, getText } = useLanguage();
  const navigate = useNavigate();
  const slug = user?.tenant_slug;
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  // Restore filters from sessionStorage so they survive going into a lesson & back
  const initialFilters = (() => {
    try { return JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || '{}'); }
    catch (e) { return {}; }
  })();
  const [searchQuery, setSearchQuery] = useState(initialFilters.search || '');
  const [classFilter, setClassFilter] = useState(initialFilters.classFilter || 'all');
  const [subjectFilter, setSubjectFilter] = useState(initialFilters.subjectFilter || 'all');
  
  // Modal states
  const [assignTeachersModalOpen, setAssignTeachersModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Persist filters whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        search: searchQuery, classFilter, subjectFilter
      }));
    } catch (e) { /* ignore */ }
  }, [searchQuery, classFilter, subjectFilter]);

  const loadData = async () => {
    try {
      const promises = [
        courseAPI.listCourses({ tenant_id: user?.tenant_id }),
        courseAPI.listSubjects({ tenant_id: user?.tenant_id }),
        courseAPI.listClasses({ tenant_id: user?.tenant_id }),
        userAPI.listTeachers({ tenant_id: user?.tenant_id })
      ];
      const results = await Promise.allSettled(promises);
      setCourses(results[0].status === 'fulfilled' ? (results[0].value.data || []) : []);
      setSubjects(results[1].status === 'fulfilled' ? (results[1].value.data || []) : []);
      setClasses(results[2].status === 'fulfilled' ? (results[2].value.data || []) : []);
      const tRes = results[3];
      setTeachers(tRes.status === 'fulfilled' ? (tRes.value.data?.items || tRes.value.data || []) : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) return;

    setSaving(true);
    try {
      await courseAPI.deleteCourse(selectedCourse.id);
      toast.success(language === 'fr' ? 'Cours supprimé!' : 'Course deleted!');
      setDeleteModalOpen(false);
      setSelectedCourse(null);
      loadData();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    } finally {
      setSaving(false);
    }
  };

  const openAssignTeachersModal = (course) => {
    setSelectedCourse(course);
    setSelectedTeacherIds(course.teacher_ids || (course.teacher_id ? [course.teacher_id] : []));
    setAssignTeachersModalOpen(true);
  };

  const handleAssignTeachers = async () => {
    setSaving(true);
    try {
      await courseAPI.assignTeachers(selectedCourse.id, selectedTeacherIds);
      toast.success(language === 'fr' ? 'Enseignants assignés avec succès!' : 'Teachers assigned successfully!');
      setAssignTeachersModalOpen(false);
      setSelectedCourse(null);
      setSelectedTeacherIds([]);
      loadData();
    } catch (error) {
      console.error('Error assigning teachers:', error);
      toast.error(language === 'fr' ? 'Erreur d\'assignation' : 'Assignment error');
    } finally {
      setSaving(false);
    }
  };

  const toggleTeacherSelection = (teacherId) => {
    setSelectedTeacherIds(prev => 
      prev.includes(teacherId) 
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  // Filter courses by search, class, subject — and for teachers, only their assigned courses
  const isTeacher = user?.role === 'teacher';
  const basePath = isTeacher ? buildTenantPath(slug, 'teacher') : buildTenantPath(slug, 'school');
  // Teacher assignments reference the TEACHER PROFILE id, not the user account id
  const myTeacherId = isTeacher ? teachers.find(t => t.user_id === user?.id)?.id : null;
  const filteredCourses = courses.filter(c => {
    // Teacher restriction
    if (isTeacher) {
      const ids = c.teacher_ids || (c.teacher_id ? [c.teacher_id] : []);
      if (!ids.includes(myTeacherId) && !ids.includes(user.id)) return false;
    }
    // Search filter
    const q = searchQuery.toLowerCase();
    if (q && !(c.title_fr?.toLowerCase().includes(q) || c.title?.toLowerCase().includes(q))) {
      return false;
    }
    // Subject filter
    if (subjectFilter !== 'all' && c.subject_id !== subjectFilter) return false;
    // Class filter (course → subject → class)
    if (classFilter !== 'all') {
      const subj = subjects.find(s => s.id === c.subject_id);
      if (!subj || subj.class_id !== classFilter) return false;
    }
    return true;
  });

  // Subjects available for the selected class (for cascading filter)
  const availableSubjects = classFilter === 'all' 
    ? subjects 
    : subjects.filter(s => s.class_id === classFilter);

  const getSubjectClassName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (!subject) return '-';
    const cls = classes.find(c => c.id === subject.class_id);
    return cls ? `${subject.name_fr || subject.name} - ${cls.name_fr || cls.name}` : (subject.name_fr || subject.name);
  };

  const getTeacherNames = (course) => {
    const ids = course.teacher_ids || (course.teacher_id ? [course.teacher_id] : []);
    if (ids.length === 0) return language === 'fr' ? 'Non assigné' : 'Not assigned';
    const names = ids.map(id => {
      const teacher = teachers.find(t => t.id === id || t.user_id === id);
      if (!teacher) return null;
      // Handle case where first_name/last_name might be null
      const firstName = teacher.first_name || '';
      const lastName = teacher.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      // If no name, use specialization or email
      return fullName || teacher.specialization || teacher.email || null;
    }).filter(Boolean);
    return names.length > 0 ? names.join(', ') : (language === 'fr' ? 'Non assigné' : 'Not assigned');
  };

  return (
    <div className="space-y-6" data-testid="course-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {language === 'fr' ? 'Gestion des Cours' : 'Course Management'}
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? `${filteredCourses.length} cours disponible(s)`
              : `${filteredCourses.length} course(s) available`
            }
          </p>
        </div>
        <Button 
          className="bg-emerald-700 hover:bg-emerald-800" 
          onClick={() => navigate(`${basePath}/courses/new`)}
          data-testid="add-course-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Créer un Cours' : 'Create Course'}
        </Button>
      </div>

      {/* Search & Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="relative md:col-span-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder={language === 'fr' ? 'Rechercher un cours...' : 'Search courses...'}
                className="pl-9" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="course-search-input"
              />
            </div>
            <div className="md:col-span-4">
              <Select 
                value={classFilter} 
                onValueChange={(v) => { setClassFilter(v); setSubjectFilter('all'); }}
              >
                <SelectTrigger data-testid="class-filter-select">
                  <SelectValue placeholder={language === 'fr' ? 'Toutes les classes' : 'All classes'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {language === 'fr' ? 'Toutes les classes' : 'All classes'}
                  </SelectItem>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name_fr || cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                <SelectTrigger data-testid="subject-filter-select">
                  <SelectValue placeholder={language === 'fr' ? 'Toutes les matières' : 'All subjects'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {language === 'fr' ? 'Toutes les matières' : 'All subjects'}
                  </SelectItem>
                  {availableSubjects.map(subj => (
                    <SelectItem key={subj.id} value={subj.id}>
                      {subj.name_fr || subj.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(classFilter !== 'all' || subjectFilter !== 'all' || searchQuery) && (
            <div className="flex items-center gap-2 mt-3 text-sm">
              <span className="text-slate-500">
                {language === 'fr' ? 'Filtres actifs' : 'Active filters'}:
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-emerald-700 hover:text-emerald-800"
                onClick={() => { setClassFilter('all'); setSubjectFilter('all'); setSearchQuery(''); }}
                data-testid="clear-filters-btn"
              >
                {language === 'fr' ? 'Réinitialiser' : 'Reset'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Course Grid */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : filteredCourses.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-4">
              {isTeacher 
                ? (language === 'fr' ? 'Aucun cours assigné pour le moment' : 'No assigned courses yet')
                : (language === 'fr' ? 'Aucun cours trouvé' : 'No courses found')
              }
            </p>
            {!isTeacher && (
              <Button onClick={() => navigate(`${basePath}/courses/new`)}>
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Créer votre premier cours' : 'Create your first course'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCourses.map((course) => (
            <Card key={course.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base font-medium truncate">
                        {getText(course, 'title')}
                      </CardTitle>
                      <p className="text-xs text-slate-500 truncate">
                        {getSubjectClassName(course.subject_id)}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="flex-shrink-0">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`${basePath}/courses/${course.id}`)}>
                        <Eye className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Voir' : 'View'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`${basePath}/courses/${course.id}/edit`)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Modifier' : 'Edit'}
                      </DropdownMenuItem>
                      {!isTeacher && (
                        <>
                          <DropdownMenuItem onClick={() => openAssignTeachersModal(course)}>
                            <UserCog className="w-4 h-4 mr-2" />
                            {language === 'fr' ? 'Assigner Enseignants' : 'Assign Teachers'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedCourse(course);
                              setDeleteModalOpen(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {language === 'fr' ? 'Supprimer' : 'Delete'}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-2 mb-3">
                  {(course.order || 0) > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                      #{course.order}
                    </Badge>
                  )}
                  <Badge className={course.is_published 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-slate-100 text-slate-600'
                  }>
                    {course.is_published 
                      ? (language === 'fr' ? 'Publié' : 'Published')
                      : (language === 'fr' ? 'Brouillon' : 'Draft')
                    }
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-700">
                    {course.total_lessons || 0} {language === 'fr' ? 'leçons' : 'lessons'}
                  </Badge>
                </div>
                
                <div className="text-sm text-slate-600 mb-3">
                  <span className="font-medium">{language === 'fr' ? 'Enseignants:' : 'Teachers:'}</span>
                  <span className="ml-1">{getTeacherNames(course)}</span>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => navigate(`${basePath}/courses/${course.id}/edit`)}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    {language === 'fr' ? 'Éditer' : 'Edit'}
                  </Button>
                  {!isTeacher && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openAssignTeachersModal(course)}
                    >
                      <Link2 className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'Assigner' : 'Assign'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assign Teachers Modal */}
      <Dialog open={assignTeachersModalOpen} onOpenChange={setAssignTeachersModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' 
                ? `Assigner des enseignants à "${selectedCourse?.title_fr || selectedCourse?.title}"`
                : `Assign teachers to "${selectedCourse?.title_fr || selectedCourse?.title}"`
              }
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Sélectionnez les enseignants qui peuvent gérer ce cours (relation n:n)'
                : 'Select the teachers who can manage this course (n:n relationship)'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 max-h-[400px] overflow-y-auto">
            {teachers.length === 0 ? (
              <p className="text-center text-slate-500 py-4">
                {language === 'fr' ? 'Aucun enseignant disponible' : 'No teachers available'}
              </p>
            ) : (
              <div className="space-y-2">
                {teachers.map((teacher) => {
                  const teacherName = `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() || teacher.specialization || teacher.email || 'Unknown';
                  return (
                  <div 
                    key={teacher.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer ${
                      selectedTeacherIds.includes(teacher.id) ? 'border-emerald-300 bg-emerald-50' : ''
                    }`}
                    onClick={() => toggleTeacherSelection(teacher.id)}
                  >
                    <Checkbox 
                      checked={selectedTeacherIds.includes(teacher.id)}
                      onCheckedChange={() => toggleTeacherSelection(teacher.id)}
                    />
                    <UserCog className="w-5 h-5 text-amber-600" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{teacherName}</p>
                      <p className="text-xs text-slate-500">
                        {teacher.specialization || teacher.email}
                      </p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignTeachersModalOpen(false); setSelectedCourse(null); setSelectedTeacherIds([]); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleAssignTeachers}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Assigner' : 'Assign'} ({selectedTeacherIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {language === 'fr' ? 'Supprimer le Cours' : 'Delete Course'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer "${selectedCourse?.title_fr || selectedCourse?.title}"? Cette action supprimera également toutes les leçons associées.`
                : `Are you sure you want to delete "${selectedCourse?.title_fr || selectedCourse?.title}"? This will also delete all associated lessons.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleDeleteCourse}
              disabled={saving}
              variant="destructive"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
